import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const TOOLTIP_MAX_WIDTH = 280;
const CURSOR_OFFSET_Y = 24;
const SCROLL_THRESHOLD_PX = 10;

interface PointerPos { x: number; y: number }

// Module-level pointer tracker — always current regardless of when individual
// portal instances mount, eliminating the race condition where a fresh portal
// could miss the event that triggered it.
let latestPointer: PointerPos | null = null;

// Tracks which chart containers have been scroll- or externally-dismissed.
// Using a WeakSet means:
//   - Dismissed state survives nivo unmounting/remounting the portal between
//     interactions (the chart container element is the stable anchor).
//   - No memory leak: entries are automatically collected when charts unmount.
const dismissedContainers = new WeakSet<Element>();

if (typeof window !== "undefined") {
  window.addEventListener(
    "mousemove",
    (e: MouseEvent) => { latestPointer = { x: e.clientX, y: e.clientY }; },
    { passive: true },
  );
  window.addEventListener(
    "touchstart",
    (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) latestPointer = { x: t.clientX, y: t.clientY };
    },
    { passive: true },
  );
  window.addEventListener(
    "touchmove",
    (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) latestPointer = { x: t.clientX, y: t.clientY };
    },
    { passive: true },
  );
}

interface ChartTooltipPortalProps {
  children: React.ReactNode;
  // Viewport X to centre the tooltip on. Falls back to cursor X when omitted.
  anchorX?: number;
  // Ref to the chart's container element — used to scope dismiss/restore logic
  // to this specific chart.
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

const ChartTooltipPortal: React.FC<ChartTooltipPortalProps> = ({ children, anchorX, containerRef }) => {
  // Initialise immediately from the module-level tracker. If this chart was
  // previously scroll-dismissed, start null so the portal stays hidden even
  // across a nivo-triggered remount.
  const [mouse, setMouse] = useState<PointerPos | null>(() => {
    if (containerRef?.current && dismissedContainers.has(containerRef.current)) return null;
    return latestPointer;
  });

  // Records where the finger landed so we can measure scroll displacement.
  const touchOriginRef = useRef<PointerPos | null>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      // Don't re-show via synthesised mousemove if this chart is dismissed.
      if (containerRef?.current && dismissedContainers.has(containerRef.current)) return;
      setMouse({ x: e.clientX, y: e.clientY });
    };

    const onTouchStart = (e: TouchEvent) => {
      const target = e.target as Node | null;
      const isInsideChart = containerRef?.current
        ? containerRef.current.contains(target)
        : true;

      const t = e.touches[0];
      if (t) touchOriginRef.current = { x: t.clientX, y: t.clientY };

      if (isInsideChart) {
        // Touch landed on this chart — clear any prior dismissal and show.
        if (containerRef?.current) dismissedContainers.delete(containerRef.current);
        if (t) setMouse({ x: t.clientX, y: t.clientY });
      } else {
        // Touch landed elsewhere — dismiss and block the upcoming synthesised
        // mousemove from bringing the portal back.
        if (containerRef?.current) dismissedContainers.add(containerRef.current);
        setMouse(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !touchOriginRef.current) return;

      // Any deliberate scroll (regardless of where the touch started) should
      // close the tooltip. The WeakSet prevents the portal from flickering back
      // open if nivo remounts it during or after the scroll gesture.
      const dy = Math.abs(t.clientY - touchOriginRef.current.y);
      if (dy > SCROLL_THRESHOLD_PX) {
        if (containerRef?.current) dismissedContainers.add(containerRef.current);
        setMouse(null);
      }
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    // Capture phase ensures our dismissal runs before nivo's bubble-phase
    // handlers — preventing the brief tooltip flash on a new bar during scroll.
    window.addEventListener("touchmove", onTouchMove, { passive: true, capture: true });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
    };
  }, [containerRef]);

  if (!mouse) return null;

  const targetX = anchorX ?? mouse.x;
  const left = Math.max(
    8 + TOOLTIP_MAX_WIDTH / 2,
    Math.min(targetX, window.innerWidth - 8 - TOOLTIP_MAX_WIDTH / 2),
  );

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        left,
        top: mouse.y - CURSOR_OFFSET_Y,
        transform: "translate(-50%, -100%)",
        zIndex: 99999,
        pointerEvents: "none",
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default ChartTooltipPortal;
